import { createAsyncThunk } from '@reduxjs/toolkit';
import { UserType } from '../../types/user/user.type';
import axios from 'axios';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import { setShowModal, setModalContent } from '../utils/utils.slice';
import { getCurrentUser } from '../auth/auth.action';
import NetworkService from '../../utils/network.service';
import { setTokenChecked, setUserAccessList as setAuthUserAccessList } from '../auth/auth.slice';
import { VerifiedNumberType } from '../../types/verified-number/verified-number.type';
import { VerifiedEmailType } from '../../types/verified-email/verified-email.type';
import { UserActivationCodeType } from '../../types/user/user-activation-code.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const postUserDetails = createAsyncThunk<UserType, { data: unknown }, { state: RootReducerState }>(
  'users/postUserDetails',
  async ({ data }, thunkAPI) => {
    try {
      const response = await axios.post<UserType>(`${process.env.REACT_APP_API_URL}/users`, data);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating user!' });
    }
  }
);

export const postAgreementUserDetails = createAsyncThunk<
  UserType,
  { data: unknown; fetchCurrentUser?: boolean },
  { state: RootReducerState }
>('users/postAgreementUserDetails', async ({ data, fetchCurrentUser }, thunkAPI) => {
  try {
    const response = await axios.post<UserType>(`${process.env.REACT_APP_API_URL}/agreements`, data);
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(setShowModal(false));

    if (fetchCurrentUser) {
      thunkAPI.dispatch(getCurrentUser({ init: false }));
    }

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during accepting agreement!' });
  }
});

export const getUsersList = createAsyncThunk<
  PaginationType<UserType>,
  RouteParamsType & {
    controller?: AbortController;
  },
  { state: RootReducerState }
>('users/getUsersList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<UserType>>(
      `${process.env.REACT_APP_API_URL}/users${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching users!' });
  }
});

export const getUsersForAssignments = createAsyncThunk<UserType[], void, { state: RootReducerState }>(
  'users/getUsersForAssignments/no-loader',
  async (_data, thunkAPI) => {
    try {
      const response = await axios.get<UserType[]>(`${process.env.REACT_APP_API_URL}/users/assignments`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching TelCloud users!' });
    }
  }
);

export const getUserDetails = createAsyncThunk<UserType, { id: number }, { state: RootReducerState }>(
  'users/getUserDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<UserType>(`${process.env.REACT_APP_API_URL}/users/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching user!' });
    }
  }
);

export const patchUserDetails = createAsyncThunk<UserType, { id: number; data: unknown }, { state: RootReducerState }>(
  'users/patchUserDetails',
  async ({ data, id }, thunkAPI) => {
    try {
      await axios.patch<UserType>(`${process.env.REACT_APP_API_URL}/users/${id}`, data);

      const response = await thunkAPI.dispatch(getUserDetails({ id })).unwrap();
      return response;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during changing user!' });
    }
  }
);

export const deleteUserDetails = createAsyncThunk<
  { success: boolean; fetchList: boolean },
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('users/deleteUserDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<{ success: boolean }>(`${process.env.REACT_APP_API_URL}/users/${id}`);
    if (fetchList) thunkAPI.dispatch(getUsersList(thunkAPI.getState().usersReducer.usersRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return { ...response.data, fetchList };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting user!' });
  }
});

export const deleteAgreementUserDetails = createAsyncThunk<
  { success: boolean; fetchList: boolean },
  { userId: number; version: string; type: string; fetchList: boolean },
  { state: RootReducerState }
>('users/deleteAgreementUserDetails', async ({ userId, type, version, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/agreements?user_id=${userId}&version=${version}&type=${type}`);
    if (fetchList) thunkAPI.dispatch(getUsersList(thunkAPI.getState().usersReducer.usersRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return { ...response.data, fetchList };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting agreement!' });
  }
});

export const patchUserPasswordAsSuperAdmin = createAsyncThunk<
  void,
  { id: number; data: unknown },
  { state: RootReducerState; rejectValue: string }
>('users/patchUserPasswordAsSuperAdmin', async ({ data, id }, thunkAPI) => {
  try {
    await axios.patch<UserType>(`${process.env.REACT_APP_API_URL}/users/${id}/changePassword`, data);
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during changing user password!' });
  }
});

export const patchUserPassword = createAsyncThunk<
  void,
  { id: number; data: unknown },
  { state: RootReducerState; rejectValue: string }
>('users/patchUserPassword', async ({ data, id }, thunkAPI) => {
  try {
    await axios.patch<UserType>(`${process.env.REACT_APP_API_URL}/users/changePassword`, data);
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(getUserDetails({ id }));
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during changing password!' });
  }
});

export const getUserAuditLogsList = createAsyncThunk<PaginationType<any>, RouteParamsType, { state: RootReducerState }>(
  'users/getUserAuditLogsList',
  async (params, thunkAPI) => {
    try {
      const queryString = Object.keys(params)
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<PaginationType<any>>(
        `${process.env.REACT_APP_API_URL}/tools/audit-logs${queryString ? `?${queryString}` : ''}`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching Audit Logs!' });
    }
  }
);

export const getUserRoles = createAsyncThunk<
  Array<{ id: string; name: string }>,
  { user_id?: number; org_id: number },
  { state: RootReducerState }
>('users/getUserRoles', async (params, thunkAPI) => {
  try {
    if (!params.user_id) delete params.user_id;

    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<Array<{ id: string; name: string }>>(
      `${process.env.REACT_APP_API_URL}/users/roles${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting roles list!' });
  }
});

export const getAPICredentialsDetails = createAsyncThunk<
  unknown,
  { id: number; orgId: number },
  { state: RootReducerState }
>('users/getAPICredentialsDetails', async ({ id, orgId }, thunkAPI) => {
  try {
    const response = await axios.get<unknown>(`${process.env.REACT_APP_API_URL}/api-credentials/${orgId}/${id}`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting API Credentials!' });
  }
});

export const postAPICredentialsDetails = createAsyncThunk<
  unknown,
  { data: { organizationID: number; createdByID: number } },
  { state: RootReducerState }
>('users/postAPICredentialsDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<unknown>(`${process.env.REACT_APP_API_URL}/api-credentials`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating API Credentials!' });
  }
});

export const patchAPICredentialsDetails = createAsyncThunk<
  unknown,
  { id: number; orgId: number },
  { state: RootReducerState }
>('users/patchAPICredentialsDetails', async ({ orgId, id }, thunkAPI) => {
  try {
    const response = await axios.patch<unknown>(`${process.env.REACT_APP_API_URL}/api-credentials/${orgId}/${id}`, {});
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating API Credentials!' });
  }
});

export const deleteAPICredentialsDetails = createAsyncThunk<
  { success: boolean; fetchList: boolean },
  { orgId: number; id: number; fetchList: boolean },
  { state: RootReducerState }
>('users/deleteAPICredentialsDetails', async ({ orgId, id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/api-credentials/${orgId}/${id}`);
    if (fetchList) thunkAPI.dispatch(getUsersList(thunkAPI.getState().usersReducer.usersRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return { ...response.data, fetchList };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting API Credentials!' });
  }
});

export const getUserAccessList = createAsyncThunk<
  Array<{ id: string; name: string }>,
  { user_id: number },
  { state: RootReducerState }
>('users/getUserAccessList', async (params, thunkAPI) => {
  try {
    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<Array<{ id: string; name: string }>>(
      `${process.env.REACT_APP_API_URL}/user-access${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting user access list!' });
  }
});

export const postUserAccess = createAsyncThunk<
  unknown,
  { data: { user: { id: number }; organization: { id: number }; role?: string } },
  { state: RootReducerState }
>('users/postUserAccess', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<unknown>(`${process.env.REACT_APP_API_URL}/user-access`, data);
    const userId = thunkAPI.getState().usersReducer.userDetails?.id;
    if (userId) {
      thunkAPI.dispatch(getUserAccessList({ user_id: userId }));
      thunkAPI.dispatch(getUserAccessWithRoles({ user_id: userId }));
    }
    const navResp = await axios.get(`${process.env.REACT_APP_API_URL}/user-access?user_id=${userId}`);
    thunkAPI.dispatch(setAuthUserAccessList(navResp.data));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating new access!' });
  }
});

export const postSwitchUserOrganization = createAsyncThunk<
  unknown,
  { data: { organization: { id: number } } },
  { state: RootReducerState }
>('users/postSwitchUserOrganization', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{
      access_token: string;
    }>(`${process.env.REACT_APP_API_URL}/user-access/switchToOrganization`, data);

    localStorage.setItem('token', response.data.access_token);
    NetworkService.setupDefaultHeaders(response.data.access_token);
    thunkAPI.dispatch(setTokenChecked(true));
    thunkAPI.dispatch(getCurrentUser({ init: false }));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(setShowModal(false));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting organization list!' });
  }
});

export const deleteUserAccessDetails = createAsyncThunk<
  { success: boolean; fetchList: boolean },
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('users/deleteUserAccessDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/user-access/${id}`);
    const userId = thunkAPI.getState().usersReducer.userDetails?.id;
    if (fetchList && userId) {
      thunkAPI.dispatch(getUserAccessList({ user_id: userId }));
      thunkAPI.dispatch(getUserAccessWithRoles({ user_id: userId }));
    }
    const navResp = await axios.get(`${process.env.REACT_APP_API_URL}/user-access?user_id=${userId}`);
    thunkAPI.dispatch(setAuthUserAccessList(navResp.data));

    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return { ...response.data, fetchList };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during removing user access!' });
  }
});

export const postVerifiedNumber = createAsyncThunk<
  VerifiedNumberType,
  { data: { number: string; number_country_code: string; number_dial_code: string } },
  { state: RootReducerState }
>('users/postVerifiedNumber', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<VerifiedNumberType>(`${process.env.REACT_APP_API_URL}/verification/number`, data);

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating Number!' });
  }
});

export const postVerifiedEmail = createAsyncThunk<
  VerifiedEmailType,
  { data: { email: string } },
  { state: RootReducerState }
>('users/postVerifiedEmail', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<VerifiedEmailType>(`${process.env.REACT_APP_API_URL}/verification/email`, data);
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating Email!' });
  }
});

export const deleteVerifiedEmailDetails = createAsyncThunk<
  { success: boolean },
  { id: number },
  { state: RootReducerState }
>('users/deleteVerifiedEmailDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.delete<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/verification/email/${id}`);
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return { ...response.data };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during removing Email!' });
  }
});

export const deleteVerifiedNumberDetails = createAsyncThunk<
  { success: boolean },
  { id: number },
  { state: RootReducerState }
>('users/deleteVerifiedNumberDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.delete<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/verification/number/${id}`);
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return { ...response.data };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during removing Number!' });
  }
});

export const postVerifyVerifiedEmail = createAsyncThunk<
  { success: boolean },
  { data: unknown },
  { state: RootReducerState }
>('users/postVerifyVerifiedEmail', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/verification/verify/email/`, data);
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return { ...response.data };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during verifying Email!' });
  }
});

export const postVerifyVerifiedNumber = createAsyncThunk<
  { success: boolean },
  { data: { id: number; code: string } },
  {
    state: RootReducerState;
  }
>('users/postVerifyVerifiedNumber', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/verification/verify/number/`, data);
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return { ...response.data };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during verifying Number!' });
  }
});

export const postResendVerifiedEmail = createAsyncThunk<
  { success: boolean },
  { data: unknown; id: number },
  { state: RootReducerState }
>('users/postResendVerifiedEmail', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/verification/resend/email/${id}`, data);
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return { ...response.data };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during sending Email!' });
  }
});

export const postResendVerifiedNumber = createAsyncThunk<
  { success: boolean },
  { data: unknown; id: number },
  { state: RootReducerState }
>('users/postResendVerifiedNumber', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/verification/resend/number/${id}`, data);

    return { ...response.data };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during sending Number!' });
  }
});

export const postUsersBulk = createAsyncThunk<
  {
    success: any[];
    errors: Array<{ email: string; error: string; row: number }>;
    summary: { total: number; succeeded: number; failed: number };
  },
  { organization: { id: number }; users: Array<any> },
  { state: RootReducerState }
>('users/postUsersBulk', async (data, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: any[];
      errors: Array<{ email: string; error: string; row: number }>;
      summary: { total: number; succeeded: number; failed: number };
    }>(`${process.env.REACT_APP_API_URL}/users/bulk`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating users!' });
  }
});

export const getUserOrgRole = createAsyncThunk<
  { role: string | null },
  { userId: number; orgId: number },
  { state: RootReducerState }
>('users/getUserOrgRole', async ({ userId, orgId }, thunkAPI) => {
  try {
    const response = await axios.get<{ role: string | null }>(
      `${process.env.REACT_APP_API_URL}/user-access/role?userId=${userId}&orgId=${orgId}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error getting user role for organization!' });
  }
});

export const setUserOrgRole = createAsyncThunk<
  { success: boolean },
  { userId: number; orgId: number; role: string },
  { state: RootReducerState }
>('users/setUserOrgRole', async ({ userId, orgId, role }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: boolean }>(`${process.env.REACT_APP_API_URL}/user-access/role`, {
      userId,
      orgId,
      role,
    });
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error setting user role for organization!' });
  }
});

export const getUserAccessWithRoles = createAsyncThunk<
  Array<{ id: number; role: string | null; organization: any }>,
  { user_id: number },
  { state: RootReducerState }
>('users/getUserAccessWithRoles', async ({ user_id }, thunkAPI) => {
  try {
    const response = await axios.get<Array<{ id: number; role: string | null; organization: any }>>(
      `${process.env.REACT_APP_API_URL}/user-access?user_id=${user_id}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error getting user access with roles!' });
  }
});

export const resendUserInvitation = createAsyncThunk(
  'users/resendInvitation',
  async ({ email }: { email: string }, thunkAPI) => {
    try {
      const response = await axios.post<{ success: true }>(`${process.env.REACT_APP_API_URL}/users/resend-invitation`, {
        email,
      });
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error resending user invitation!' });
    }
  }
);

export const generateUserActivationCode = createAsyncThunk(
  'users/generateActivationCode',
  async ({ userId }: { userId: number }, thunkAPI) => {
    try {
      const response = await axios.post<UserActivationCodeType>(
        `${process.env.REACT_APP_API_URL}/users/${userId}/generate-activation-code`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error generating activation code!' });
    }
  }
);

export const generateOktaActivationLink = createAsyncThunk<
  { activationLink: string },
  { userId: number },
  { state: RootReducerState; rejectValue: string }
>('users/generateOktaActivationLink/no-loader', async ({ userId }, thunkAPI) => {
  try {
    const response = await axios.post<{ activationLink: string }>(
      `${process.env.REACT_APP_API_URL}/users/${userId}/generate-okta-activation-link`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error generating Okta activation link!' });
  }
});

export const activateAccountWithCode = createAsyncThunk(
  'users/activateWithCode',
  async (data: { email: string; password: string; activationCode: string }, thunkAPI) => {
    try {
      const response = await axios.post<{ success: true }>(
        `${process.env.REACT_APP_API_URL}/users/activate-with-code`,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error while activating the user!' });
    }
  }
);
